// 仪表盘：/api/dashboard 聚合卡片。
// - health banner（红/黄/绿 综合）
// - ops metrics
// - 在线 agents / 在线 instances 摘要
// 30s 轮询；SSE 事件（任意类型）触发 invalidate。

import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "@/api/client";
import { API_DASHBOARD } from "@/api/endpoints";
import type { DashboardResponse } from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { useSSESubscription } from "@/hooks/useSSESubscription";

export const Route = createFileRoute("/")({
  component: DashboardRouteComponent,
});

function DashboardRouteComponent() {
  return <DashboardView />;
}

type HealthLevel = "ok" | "warn" | "danger";

function computeHealth(data: DashboardResponse): HealthLevel {
  const pending =
    data.stats.pending_machines + data.stats.pending_accounts;
  if (
    data.stats.cache_apply_failed_24h > 0 ||
    data.ops.webhook_failed > 0
  ) {
    return "danger";
  }
  if (
    pending > 0 ||
    data.ops.sse_dropped_total > 0 ||
    data.ops.webhook_pending > 0 ||
    data.ops.cache_stub_drift > 0
  ) {
    return "warn";
  }
  return "ok";
}

export function DashboardView() {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiGet<DashboardResponse>(API_DASHBOARD),
    refetchInterval: 30_000,
  });

  useSSESubscription("update", () => {
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  });

  return (
    <div className="space-y-4">
      <QueryView query={q}>
        {(data) => {
          const health = computeHealth(data);
          const pending =
            data.stats.pending_machines + data.stats.pending_accounts;
          const abnormal =
            data.stats.cache_apply_failed_24h + data.ops.webhook_failed;
          return (
            <>
              <HealthBanner
                level={health}
                agentsCount={data.agents.length}
                instancesCount={data.instances.length}
                pending={pending}
                abnormal={abnormal}
              />

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle>运维指标</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-y-1 text-xs">
                      <Metric label="SSE 客户端" value={data.ops.sse_clients} />
                      <Metric
                        label="SSE 丢包"
                        value={data.ops.sse_dropped_total}
                      />
                      <Metric
                        label="apply 进行中"
                        value={data.ops.apply_running}
                      />
                      <Metric
                        label="webhook 待发"
                        value={data.ops.webhook_pending}
                      />
                      <Metric
                        label="webhook 失败"
                        value={data.ops.webhook_failed}
                      />
                      <Metric label="矩阵 drift" value={data.ops.matrix_drift} />
                      <Metric
                        label="cache stub drift"
                        value={data.ops.cache_stub_drift}
                      />
                    </dl>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>
                      在线 agent ({data.agents.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {data.agents.length === 0 ? (
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        暂无在线 agent
                      </p>
                    ) : (
                      <Table dense>
                        <TableHeader>
                          <TableRow>
                            <TableHead>agent_id</TableHead>
                            <TableHead>hostname</TableHead>
                            <TableHead>state</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.agents.map((a) => (
                            <TableRow key={a.agent_id}>
                              <TableCell className="font-mono">
                                {a.agent_id}
                              </TableCell>
                              <TableCell>{a.hostname ?? ""}</TableCell>
                              <TableCell>
                                <StatusBadge
                                  variant={
                                    a.state === "online" ? "ok" : "warn"
                                  }
                                >
                                  {a.state ?? "?"}
                                </StatusBadge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>
                      在线 instance ({data.instances.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {data.instances.length === 0 ? (
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        暂无在线 instance
                      </p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {data.instances.map((i) => (
                          <li
                            key={i.instance_id}
                            className="flex items-center gap-2"
                          >
                            <StatusBadge
                              variant={i.state === "ready" ? "ok" : "warn"}
                            >
                              {i.state ?? "?"}
                            </StatusBadge>
                            <span className="font-mono">{i.instance_id}</span>
                            <span className="text-[hsl(var(--muted-foreground))]">
                              {i.role_name ?? ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>

                <Card className="lg:col-span-3">
                  <CardHeader>
                    <CardTitle>关键计数</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded border border-[hsl(var(--border))] px-2 py-1">
                        pending machines：{data.stats.pending_machines}
                      </span>
                      <span className="rounded border border-[hsl(var(--border))] px-2 py-1">
                        pending accounts：{data.stats.pending_accounts}
                      </span>
                      <span className="rounded border border-[hsl(var(--border))] px-2 py-1">
                        cache apply 24h：ok {data.stats.cache_apply_ok_24h} / fail{" "}
                        {data.stats.cache_apply_failed_24h}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

interface HealthBannerProps {
  level: HealthLevel;
  agentsCount: number;
  instancesCount: number;
  pending: number;
  abnormal: number;
}

function HealthBanner({
  level,
  agentsCount,
  instancesCount,
  pending,
  abnormal,
}: HealthBannerProps) {
  const message =
    level === "danger"
      ? "🔴 需立即处理"
      : level === "warn"
        ? "⚠ 有待办事项"
        : "✅ 系统正常";
  const borderClass =
    level === "danger"
      ? "border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10"
      : level === "warn"
        ? "border-yellow-500 bg-yellow-500/10"
        : "border-green-500 bg-green-500/10";
  return (
    <div className={`rounded border ${borderClass} p-3 text-sm`}>
      <div className="font-medium">{message}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        <span>
          <strong>{agentsCount}</strong> agent
        </span>
        <span className="text-[hsl(var(--muted-foreground))]">·</span>
        <span>
          <strong>{instancesCount}</strong> lua
        </span>
        <span className="text-[hsl(var(--muted-foreground))]">·</span>
        <span>
          <strong>{pending}</strong> 待审
        </span>
        <span className="text-[hsl(var(--muted-foreground))]">·</span>
        <span>
          <strong>{abnormal}</strong> 异常
        </span>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <>
      <dt className="text-[hsl(var(--muted-foreground))]">{label}</dt>
      <dd className="font-mono">{value}</dd>
    </>
  );
}
